import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, fmtDate, fmtClock, fmtTime } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { useAuth } from "../lib/auth";
import { Tau, Loading } from "../components/common";

const Big = ({ label, value, accent, testid }) => (
  <div className="rounded-3xl bg-white/5 border border-white/10 p-6" data-testid={testid}>
    <div className="text-xs uppercase tracking-[0.2em] text-[#E5DEC9]/70">{label}</div>
    <div className="font-serif text-6xl xl:text-7xl font-black tabular-nums mt-2" style={{ color: accent }}>{value ?? 0}</div>
  </div>
);

export default function LiveScreen() {
  const { mid } = useParams();
  const { user } = useAuth();
  const isStaff = user.role !== "monitor";
  const { data } = usePoll(() => api.get(isStaff ? `/meetings/${mid}/live` : `/monitor/meetings/${mid}/live`).then((r) => r.data), 4000, [mid]);
  const code = usePoll(() => (isStaff ? api.get(`/meetings/${mid}/onsite-code`).then((r) => r.data) : Promise.resolve(null)), 5000, [mid]);
  if (!data) return <div className="min-h-screen bg-[#1F2A1F]"><Loading /></div>;
  const { meeting: m, stats: s } = data;
  const url = `${window.location.origin}/m/${m.id}${code.data && m.code_verification ? `?c=${code.data.code}` : ""}`;
  const maxZ = Math.max(1, ...s.by_zone.map((z) => z.count));
  return (
    <div className="min-h-screen bg-[#1F2A1F] text-[#FDFBF7] p-6 xl:p-10 grain" data-testid="live-screen">
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-[#FDFBF7] grid place-items-center"><Tau className="h-8 w-8" /></div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#E5DEC9]/70">Saint Francis of Assisi Parish · Live Attendance</div>
            <h1 className="font-serif text-3xl xl:text-4xl font-bold mt-1">{m.title}</h1>
            <div className="text-[#E5DEC9] mt-1">{fmtDate(m.date)} · {fmtClock(m.start_time)} · {m.venue} · <b className="uppercase">{m.status}</b></div>
          </div>
        </div>
        <Link to={isStaff ? `/admin/meetings/${mid}` : `/monitor/${mid}`} className="text-xs text-[#E5DEC9]/60 hover:text-white">Exit</Link>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6 mt-8">
        <div className="grid gap-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Big label="Verified Attendees" value={s.verified} accent="#86EFAC" testid="live-verified" />
            <Big label="Registrations" value={s.total_registrations} accent="#FDFBF7" />
            <Big label="Pending Verification" value={s.pending} accent="#FCD34D" />
            <Big label="GKKs Represented" value={s.gkks_represented} accent="#F4A582" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
              <div className="text-sm uppercase tracking-[0.2em] text-[#E5DEC9]/70 mb-4">By Zone</div>
              {s.by_zone.map((z) => (
                <div key={z.zone_id} className="flex items-center gap-3 py-1.5">
                  <span className="w-20 text-lg">{z.name}</span>
                  <div className="flex-1 h-7 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#C85A32] rounded-full transition-[width] duration-700" style={{ width: `${(z.count / maxZ) * 100}%` }} /></div>
                  <span className="w-12 text-right font-bold text-xl tabular-nums">{z.count}</span>
                </div>
              ))}
            </div>
            <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
              <div className="text-sm uppercase tracking-[0.2em] text-[#E5DEC9]/70 mb-4">Latest check-ins</div>
              {data.rows.filter((r) => ["present", "late", "pending"].includes(r.status)).slice(0, 8).map((r) => (
                <div key={r.id} className="flex justify-between gap-3 py-1.5 border-b border-white/5 fade-up">
                  <span className="truncate text-lg">{r.name} <span className="text-[#E5DEC9]/60 text-sm">{r.gkk || r.organization || ""}</span></span>
                  <span className="text-sm text-[#E5DEC9]/70 whitespace-nowrap">{fmtTime(r.checkin_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {isStaff && (
          <div className="rounded-3xl bg-[#FDFBF7] text-stone-900 p-6 flex flex-col items-center text-center" data-testid="live-qr-panel">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Scan to register</div>
            <QRCodeSVG value={url} size={260} className="mt-4" fgColor="#2D3B2D" />
            {m.code_verification && code.data && (
              <>
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-6">On-site code</div>
                <div className="font-mono text-5xl font-black tracking-[0.2em] text-[#5C3A21]" data-testid="live-onsite-code">{code.data.code}</div>
                <div className="text-xs text-stone-500 mt-1">Changes every minute · proves physical presence</div>
              </>
            )}
            <div className="text-xs text-stone-500 mt-4">Meeting ID {m.code}</div>
          </div>
        )}
      </div>
      <div className="mt-6 text-xs text-[#E5DEC9]/60">Official synchronized attendance · last updated {new Date(data.updated_at).toLocaleTimeString("en-PH", { timeZone: "Asia/Manila" })} · Offline entries awaiting sync (reported): {s.offline_pending_known}</div>
    </div>
  );
}
